import PropTypes from 'prop-types'
import { useCallback, useMemo, useReducer } from 'react'
import { Alert, IconButton, Snackbar } from '@mui/material'
import { Close } from '@mui/icons-material'
import SnackbarContext from './context'
import reducer, { initialState } from './reducer'

function SnackbarProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState)

  const createSnackbar = useCallback(
    /**
     *
     * @param {ReactNode} message - The message to display in the snackbar.
     * @param {"error" | "info" | "success" | "warning" | "default"} [severity='default'] - The severity level of the snackbar (e.g., 'default', 'success', 'error').
     * @param {function} [onClose]
     */
    (message, severity = 'default', onClose = null) => {
      dispatch({ type: 'SNACK/CREATE', payload: { message, severity, onClose } })
    },
    [],
  )

  const removeSnackbar = useCallback((snackId) => {
    dispatch({ type: 'SNACK/REMOVE', payload: { snackId } })
  }, [])

  const renderSnackbars = () =>
    state.amount > 0 &&
    state.snacks.map((snack) => {
      const handleClose = () => {
        if (snack?.onClose) snack.onClose()
        removeSnackbar(snack.id)
      }

      if (snack.severity === 'default') {
        return (
          <Snackbar
            data-testid={`snackbar-${snack.id}`}
            key={snack.id}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            open
            autoHideDuration={4000}
            onClose={handleClose}
            message={snack.message}
            action={
              <IconButton key="close" color="inherit" onClick={handleClose}>
                <Close />
              </IconButton>
            }
          />
        )
      }
      return (
        <Snackbar
          data-testid={`snackbar-${snack.id}`}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open
          autoHideDuration={6000}
          onClose={handleClose}
        >
          <Alert onClose={handleClose} severity={snack.severity} variant="filled">
            {snack.message}
          </Alert>
        </Snackbar>
      )
    })

  const contextValue = useMemo(() => ({ createSnackbar, state }), [createSnackbar, state])

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}
      {renderSnackbars()}
    </SnackbarContext.Provider>
  )
}

SnackbarProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export default SnackbarProvider
